/**
 * Screen Wake Lock for a live voice dialog, so a phone does not sleep mid-turn.
 * Missing or denied API is a no-op; iOS often has no wake lock.
 */

type WakeLockSentinel = { released: boolean; release: () => Promise<void> };

let sentinel: WakeLockSentinel | null = null;
/** Bumps on every acquire and release so a lock that resolves after teardown is dropped. */
let acquireGeneration = 0;

async function releaseSentinel(held: WakeLockSentinel): Promise<void> {
  try {
    await held.release();
  } catch {
    // Already released by the browser (tab hide, lock lost).
  }
}

function wakeLockApi(): { request: (type: "screen") => Promise<WakeLockSentinel> } | null {
  if (typeof navigator === "undefined") {
    return null;
  }
  const nav = navigator as Navigator & {
    wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinel> };
  };
  return nav.wakeLock ?? null;
}

/** Hold the screen on while a voice dialog is live and not paused. */
export async function requestVoiceWakeLock(): Promise<void> {
  const api = wakeLockApi();
  if (api === null) {
    return;
  }
  if (sentinel !== null && !sentinel.released) {
    return;
  }
  const generation = ++acquireGeneration;
  try {
    const acquired = await api.request("screen");
    if (generation !== acquireGeneration) {
      await releaseSentinel(acquired);
      return;
    }
    sentinel = acquired;
  } catch {
    if (generation === acquireGeneration) {
      sentinel = null;
    }
  }
}

/** Drop the screen lock. Safe to call when none is held. */
export async function releaseVoiceWakeLock(): Promise<void> {
  acquireGeneration += 1;
  const held = sentinel;
  sentinel = null;
  if (held === null || held.released) {
    return;
  }
  await releaseSentinel(held);
}
